import { z } from "zod"
import { Address, isAddress } from "viem"
import {
  SyntheticTypeEnum,
  ProjectConfigSyntheticShema,
  type ProjectConfigSyntheticType,
} from "@tutellus/dappcomposer-contracts-ifaces"

const ProjectConfigSyntheticValidation = z.object({
  id: z.string({ required_error: "id is required" }),
  escrowedTokenId: z.string({ required_error: "escrowedTokenId is required" }),
  type: z.nativeEnum(SyntheticTypeEnum),
  voterAddress: z.string({ required_error: "voterAddress is required" }).refine(isAddress),
  rebaseAddress: z.string({ required_error: "rebaseAddress is required" }).refine(isAddress),
  minterAddress: z.string({ required_error: "minterAddress is required" }).refine(isAddress),
  vaultAddress: z.string({ required_error: "vaultAddress is required" }).refine(isAddress).optional(),
  epochDuration: z.number({ required_error: "epochDuration is required" }).optional(),
  minLockDuration: z.number({ required_error: "minLockDuration is required" }).optional(),
  maxLockDuration: z.number({ required_error: "maxLockDuration is required" }).optional(),
})

export class ProjectConfigSynthetic {
  constructor(
    private readonly _id: string,
    private readonly _escrowedTokenId: string,
    private readonly _type: SyntheticTypeEnum,
    private readonly _voterAddress: Address,
    private readonly _rebaseAddress: Address,
    private readonly _minterAddress: Address,
    private readonly _vaultAddress?: Address,
    private readonly _epochDuration?: number,
    private readonly _minLockDuration?: number,
    private readonly _maxLockDuration?: number,
  ) {}

  static create({
    id,
    escrowedTokenId,
    type,
    voterAddress,
    rebaseAddress,
    minterAddress,
    vaultAddress,
    epochDuration,
    minLockDuration,
    maxLockDuration,
  }: z.infer<typeof ProjectConfigSyntheticValidation>) {
    const { success, error } = ProjectConfigSyntheticValidation.safeParse({
      id,
      escrowedTokenId,
      type,
      voterAddress,
      rebaseAddress,
      minterAddress,
      vaultAddress,
      epochDuration,
      minLockDuration,
      maxLockDuration,
    })
    if (!success) throw new Error(error.message)

    return new ProjectConfigSynthetic(
      id,
      escrowedTokenId,
      type,
      voterAddress,
      rebaseAddress,
      minterAddress,
      vaultAddress,
      epochDuration,
      minLockDuration,
      maxLockDuration,
    )
  }

  static createFromAPI({
    escrowedTokenId,
    id,
    type,
    epochDuration,
    maxLockDuration,
    minLockDuration,
    vaultAddress,
    voterAddress,
    rebaseAddress,
    minterAddress,
  }: ProjectConfigSyntheticType) {
    const { success, error } = ProjectConfigSyntheticShema.safeParse({
      escrowedTokenId,
      id,
      type,
      epochDuration,
      maxLockDuration,
      minLockDuration,
      vaultAddress,
      voterAddress,
      rebaseAddress,
      minterAddress,
    })

    if (!success) throw new Error(error.message)

    return ProjectConfigSynthetic.create({
      id,
      escrowedTokenId,
      type,
      voterAddress: voterAddress as Address,
      rebaseAddress: rebaseAddress as Address,
      minterAddress: minterAddress as Address,
      vaultAddress: (vaultAddress as Address) ?? undefined,
      epochDuration: epochDuration ?? undefined,
      minLockDuration: minLockDuration ?? undefined,
      maxLockDuration: maxLockDuration ?? undefined,
    })
  }

  get id () { return this._id } // prettier-ignore
  get escrowedTokenId () { return this._escrowedTokenId } // prettier-ignore
  get voterAddress () { return this._voterAddress } // prettier-ignore
  get rebaseAddress () { return this._rebaseAddress } // prettier-ignore
  get minterAddress () { return this._minterAddress } // prettier-ignore
  get vaultAddress () { return this._vaultAddress } // prettier-ignore
  get type () { return this._type } // prettier-ignore
  get minDurationSeconds () { return this._minLockDuration ?? 0 } // prettier-ignore
  get maxDurationSeconds () { return this._maxLockDuration ?? 0 } // prettier-ignore
  get minDurationDays () { return this.minDurationSeconds / 60 / 60 / 24  } // prettier-ignore
  get maxDurationDays () { return this.maxDurationSeconds / 60 / 60 / 24 } // prettier-ignore
  get maxLockDate () { return new Date(Date.now() + this.maxDurationSeconds * 1000) } // prettier-ignore
  get minLockDate () { return new Date(Date.now() + this.minDurationSeconds * 1000) } // prettier-ignore
  get epochDuration () { return this._epochDuration ?? 0 } // prettier-ignore
  get numOfSteps () { return Math.round((this.maxDurationSeconds - this.minDurationSeconds) / this.epochDuration) } // prettier-ignore

  serialize() {
    return {
      escrowedTokenId: this.escrowedTokenId,
      id: this.id,
      type: this.type,
      vaultAddress: this.vaultAddress,
      voterAddress: this.voterAddress,
      rebaseAddress: this.rebaseAddress,
      minterAddress: this.minterAddress,
      epochDuration: this.epochDuration,
      minDurationSeconds: this.minDurationSeconds,
      maxDurationSeconds: this.maxDurationSeconds,
      maxDurationDays: this.maxDurationDays,
      minDurationDays: this.minDurationDays,
      minLockDate: this.minLockDate,
      maxLockDate: this.maxLockDate,
      numOfSteps: this.numOfSteps,
    }
  }
}

export type ProjectConfigSyntheticSerialized = ReturnType<ProjectConfigSynthetic["serialize"]>
